import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const RankingTracker = () => {
  const [timeRange, setTimeRange] = useState('30days');

  const timeRangeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: '1year', label: 'Last Year' }
  ];

  const rankings = [
    {
      keyword: "content management system",
      currentPosition: 12,
      previousPosition: 15,
      change: 3,
      searchVolume: "18,100",
      url: "/features",
      lastUpdated: "2 hours ago"
    },
    {
      keyword: "cms platform",
      currentPosition: 8,
      previousPosition: 8,
      change: 0,
      searchVolume: "9,900",
      url: "/",
      lastUpdated: "2 hours ago"
    },
    {
      keyword: "seo tools for websites",
      currentPosition: 23,
      previousPosition: 28,
      change: 5,
      searchVolume: "5,400",
      url: "/seo-studio",
      lastUpdated: "2 hours ago"
    },
    {
      keyword: "website content editor",
      currentPosition: 6,
      previousPosition: 9,
      change: 3,
      searchVolume: "3,600",
      url: "/content-editor",
      lastUpdated: "2 hours ago"
    },
    {
      keyword: "analytics dashboard",
      currentPosition: 18,
      previousPosition: 16,
      change: -2,
      searchVolume: "12,100",
      url: "/analytics-hub",
      lastUpdated: "2 hours ago"
    },
    {
      keyword: "template library cms",
      currentPosition: 4,
      previousPosition: 5,
      change: 1,
      searchVolume: "2,900",
      url: "/template-library",
      lastUpdated: "2 hours ago"
    }
  ];

  const getPositionColor = (position) => {
    if (position <= 3) return 'text-green-600 bg-green-50';
    if (position <= 10) return 'text-blue-600 bg-blue-50';
    if (position <= 20) return 'text-amber-600 bg-amber-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getChangeIcon = (change) => {
    if (change > 0) return { name: 'ArrowUp', color: 'text-green-600' };
    if (change < 0) return { name: 'ArrowDown', color: 'text-red-600' };
    return { name: 'Minus', color: 'text-gray-400' };
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Icon name="TrendingUp" size={20} className="text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">Ranking Tracker</h2>
            <p className="text-sm text-gray-600">Monitor your search engine positions</p>
          </div>
        </div>
        <div className="w-full md:w-48">
          <Select
            options={timeRangeOptions}
            value={timeRange}
            onChange={setTimeRange}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={18} className="text-green-600" />
            <span className="text-sm font-medium text-green-900">Improved</span>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-green-900">4</p>
          <p className="text-xs text-green-700 mt-1">Keywords moved up</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Minus" size={18} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Stable</span>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-blue-900">1</p>
          <p className="text-xs text-blue-700 mt-1">No position change</p>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingDown" size={18} className="text-red-600" />
            <span className="text-sm font-medium text-red-900">Declined</span>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-red-900">1</p>
          <p className="text-xs text-red-700 mt-1">Keywords moved down</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-2 text-xs md:text-sm font-medium text-gray-700">Keyword</th>
              <th className="text-center py-3 px-2 text-xs md:text-sm font-medium text-gray-700">Position</th>
              <th className="text-center py-3 px-2 text-xs md:text-sm font-medium text-gray-700">Change</th>
              <th className="text-left py-3 px-2 text-xs md:text-sm font-medium text-gray-700">Volume</th>
              <th className="text-left py-3 px-2 text-xs md:text-sm font-medium text-gray-700">URL</th>
              <th className="text-left py-3 px-2 text-xs md:text-sm font-medium text-gray-700">Updated</th>
            </tr>
          </thead>
          <tbody>
            {rankings?.map((item, index) => {
              const changeInfo = getChangeIcon(item?.change);
              return (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2 text-xs md:text-sm text-gray-900 font-medium">{item?.keyword}</td>
                  <td className="py-3 px-2 text-center">
                    <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold ${getPositionColor(item?.currentPosition)}`}>
                      {item?.currentPosition}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Icon name={changeInfo?.name} size={16} className={changeInfo?.color} />
                      <span className={`text-xs md:text-sm font-semibold ${changeInfo?.color}`}>
                        {Math.abs(item?.change)}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-xs md:text-sm text-gray-600">{item?.searchVolume}</td>
                  <td className="py-3 px-2 text-xs md:text-sm text-blue-600 hover:underline cursor-pointer">{item?.url}</td>
                  <td className="py-3 px-2 text-xs text-gray-500">{item?.lastUpdated}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RankingTracker;